import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';


/**
 * --- CONFIGURATION ---
 */
const CONFIG = {
    colorVector: new THREE.Vector3(0.0, 0.23, 1.0),
    hexColor: '#003BFF',

    pointSize: 1.9,
    pointSizeMobile: 0.9,
    // Les maxPointsXxx ne sont plus lus directement : voir QUALITY plus bas.
    edgeFadeDistance: 60.0,
    lineWidth: 4,
    lineWidthMobile: 2.2,
    lineDensity: 400,
    stepSize: 2.6,
    stepIconSize: 3.2,
    stepExtrudeDepth: 0.6,
    stepFloatBase: 3.0,
    stepFloatAmplitude: 0.9,
    stepFloatSpeed: 1.4,
    stepRotationSpeed: 0.6,
    stepShadowSize: 2.2,
    stepShadowOpacity: 0.25,
    stepShineIntensity: 0.35,
    stepBaseScale: 0.6,
    stepActiveScale: 1.0,
    stepPulseScale: 0.35,
    stepPulseDecay: 2.2,
    stepFadeNear: 40,
    stepFadeFar: 300,

    scrollSpeed: 0.08, // Suivi du scroll (réduit pour ralentir la ligne)
    camInertia: 0.02,
    lookAhead: 0.05,
    camOffset: { back: 90, up: 25 },
    camOffsetMobile: { back: 65, up: 20 }, // Léger dézoom par rapport au mode "ultra-proche"
    lookAtOffsetY: 10, // Paramètre pour monter/descendre le visuel (Positif = visuel descend)
    noiseStrength: 0.2, // Force du mouvement (plus petit = moins de mouvement)

    // Zoom de la carte au début du scroll
    initialExtraDistance: 70, // Distance supplémentaire au début (0 progress) = dézoom d'entrée
    initialExtraDistanceMobile: 100, // Plus de distance sur mobile
    zoomEasePower: 300.5,       // Puissance de la courbe de zoom
    finalExtraDistance: 100,    // Dézoom de sortie (derniers 200px)

    // Plus de zones de buffer - comportement sticky comme MM-Montain-Home

    // Paramètres d'orbite (rotation libre à la fin)
    orbitSensitivity: 0.15,
    orbitSmoothing: 0.12,
    orbitAngleMax: 1.2,
    orbitPitchMin: -0.18, 
    orbitPitchMax: 0.2,
    orbitEnabledFromProgress: 0.95, // Commencer à autoriser l'orbite à 95% du progress

    // Effet gyroscope souris
    mouseParallaxStrength: 0.3, // Force de l'effet parallaxe (0 = désactivé)
    mouseSmoothing: 0.2, // Lissage du mouvement de la souris (0-1, plus petit = plus fluide)

    timeline: {
        tickSpacing: 20,
        activationThreshold: 2.5,
        steps: [],
        startYear: 0,   // Toujours 0-100 pour les pourcentages (10, 50, 90)
        endYear: 100
    }
};

let scene, camera, renderer, clock;
let mountainPoints, pathLine, pathCurve;
let stepMeshes = [];
let scrollAnchors = [];
let scrollProgress = 0;
let targetProgress = 0;
let totalLineLength = 0;
let timelineStrip;
let allLabels = [];
let allCards = [];
let loyaltyStepsContainer = null;
let stepIconGeometry;
let stepIconMaterial;
let stepIconMaterialActive;
let stepShadowGeometry;
let currentActiveCardIndex = -1;
let lastTransitionTime = 0;
const TRANSITION_THROTTLE_MS = 300; // Minimum time between transitions

const dummyLookAt = new THREE.Vector3();
const lastStableDirection = new THREE.Vector3();
const lastStableLookAt = new THREE.Vector3();

// Variables d'orbite
let isDragging = false;
let orbitAngle = 0;
let targetOrbitAngle = 0;
let orbitPitch = 0;
let targetOrbitPitch = 0;
let prevPointerX = 0;
let prevPointerY = 0;
let pointerDownX = 0;
let pointerDownY = 0;
let currentEasedProgress = 0;

// Variables parallaxe (gyroscope)
let mouseX = 0;
let mouseY = 0;
let targetMouseX = 0;
let targetMouseY = 0;

let initialized = false;
let isAnimating = false;

// --- QUALITÉ ADAPTATIVE ---
const QUALITY = (() => {
    const ua = navigator.userAgent || '';
    const isMobileUA = /mobile|android|iphone|ipad|ipod/i.test(ua);
    const cores = navigator.hardwareConcurrency || 4;
    const narrow = window.innerWidth <= 900;
    const isLowEnd = isMobileUA || narrow || cores <= 4;
    return {
        isMobile: isMobileUA || narrow,
        isLowEnd,
        // Beaucoup moins de points qu'avant (400k/100k) — gros gain CPU/GPU
        maxPoints: isMobileUA || narrow ? 18000 : (isLowEnd ? 18000 : 25000),
        pixelRatioCap: isMobileUA || narrow ?  1 : 1.15,
        antialias: !(isMobileUA || narrow) && !isLowEnd,
        circleSegments: isMobileUA || narrow ? 12 : 16,
        // Segments pour ExtrudeGeometry (shields) — 12 → 6 sans perte visible
        iconCurveSegments: isMobileUA || narrow ? 4 : 6,
        iconBevelSegments: 1,
        shadowsEnabled: false,
        idleFreezeMs: 1200
    };
})();

// --- RENDU CONDITIONNEL ---
let _mountainTime = 0;
let _lastScrollActivityAt = 0;
let _lastRenderedEasedProgress = -1;
let _lastRenderedMouseX = 0;
let _lastRenderedMouseY = 0;
let _lastRenderedOrbitA = 0;
let _lastRenderedOrbitP = 0;
let _forceRenderFrames = 3;

// --- UI THROTTLE / CACHES ---
let _lastUIYearFloat = Number.NaN;
let _lastArrowIsDown = null;
let _lastTimelineXOffset = null;
let _lastActiveLabelIndex = -2;

let _cachedWrapper = null;
let _cachedRightPanel = null;
let _cachedCanvas = null;
let _cachedNavArrow = null;
let _cachedStickyTop = 0;
let _cachedScrollableDistance = 0;
let _cachedStartScrollY = 0;
let _cachedWrapperHeight = 0;
let _cachedScrollMetrics = { progress: 0, scrollInSection: 0, inFinalDezoom: false, isInStickyRange: false, hasCompletedSection: false, wrapperHeight: 0 };

// Vecteurs réutilisables (évite des dizaines d'allocations / frame)
const _fv1 = new THREE.Vector3();
const _fv2 = new THREE.Vector3();
const _fv3 = new THREE.Vector3();
const _fv4 = new THREE.Vector3();
const _fv5 = new THREE.Vector3();
const _fv6 = new THREE.Vector3();
const _fvUp = new THREE.Vector3(0, 1, 0);
const _fvScale = new THREE.Vector3();
const _fvTmpColor = new THREE.Color();

// Helper pour détecter mobile (conservé pour la logique métier hors QUALITY)
function isMobile() {
    return QUALITY.isMobile;
}

// Helper pour obtenir les bonnes valeurs de config selon device
function getCamOffset() {
    return isMobile() ? CONFIG.camOffsetMobile : CONFIG.camOffset;
}

function getInitialExtraDistance() {
    return isMobile() ? CONFIG.initialExtraDistanceMobile : CONFIG.initialExtraDistance;
}

function getPointSize() {
    return isMobile() ? CONFIG.pointSizeMobile : CONFIG.pointSize;
}

function getLineWidth() {
    return isMobile() ? CONFIG.lineWidthMobile : CONFIG.lineWidth;
}

const STEP_SVG = `
<svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M17 12.0004C17 17.0004 13.5 19.5005 9.34 20.9505C9.12216 21.0243 8.88554 21.0207 8.67 20.9405C4.5 19.5005 1 17.0004 1 12.0004V5.00045C1 4.73523 1.10536 4.48088 1.29289 4.29334C1.48043 4.10581 1.73478 4.00045 2 4.00045C4 4.00045 6.5 2.80045 8.24 1.28045C8.45185 1.09945 8.72135 1 9 1C9.27865 1 9.54815 1.09945 9.76 1.28045C11.51 2.81045 14 4.00045 16 4.00045C16.2652 4.00045 16.5196 4.10581 16.7071 4.29334C16.8946 4.48088 17 4.73523 17 5.00045V12.0004Z" fill="black"/>
</svg>
`;

function easeInOutCubic(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/**
 * Calcule le progress 0-1 depuis la position de scroll (comme MM-Montain-Home).
 * Progress = 0 quand la section devient sticky, puis linéaire 0 → 1 pendant le scroll sticky.
 */
/**
 * Calcule le progress 0-1 depuis la position de scroll (comme MM-Montain-Home).
 * Progress = 0 quand la section devient sticky, puis linéaire 0 → 1 pendant le scroll sticky.
 */
// Recalcule les valeurs de layout (hauteurs, stickyTop) — appelé à l'init et au resize.
// Sort getComputedStyle / getBoundingClientRect / offsetHeight de la frame loop.
function refreshFidelityLayout() {
    _cachedWrapper = document.getElementById('mm-fidelity-wrapper');
    if (!_cachedWrapper) return;
    _cachedRightPanel = _cachedRightPanel || document.querySelector('#mm-fidelity-wrapper .right-panel');
    if (_cachedRightPanel) {
        const parsed = parseFloat(window.getComputedStyle(_cachedRightPanel).top);
        _cachedStickyTop = Number.isNaN(parsed) ? 0 : parsed;
    }
    _cachedWrapperHeight = _cachedWrapper.offsetHeight;
    _cachedScrollableDistance = _cachedWrapperHeight - window.innerHeight;
    const wrapperTopInDocument = _cachedWrapper.getBoundingClientRect().top + window.scrollY;
    _cachedStartScrollY = wrapperTopInDocument - _cachedStickyTop;
}

// Lit uniquement window.scrollY (ultra cheap) pour calculer le progress.
function getScrollProgress() {
    if (!_cachedWrapper) refreshFidelityLayout();
    if (!_cachedWrapper || _cachedScrollableDistance <= 0) {
        _cachedScrollMetrics.progress = 0;
        _cachedScrollMetrics.scrollInSection = 0;
        _cachedScrollMetrics.inFinalDezoom = false;
        _cachedScrollMetrics.isInStickyRange = false;
        _cachedScrollMetrics.hasCompletedSection = false;
        _cachedScrollMetrics.wrapperHeight = _cachedWrapperHeight;
        return _cachedScrollMetrics;
    }
    const endScrollY = _cachedStartScrollY + _cachedScrollableDistance;
    const scrolledInWrapper = window.scrollY - _cachedStartScrollY;

    let progress = 0;
    let isInStickyRange = false;
    let hasCompletedSection = false;

    if (window.scrollY < _cachedStartScrollY) {
        progress = 0;
    } else if (window.scrollY <= endScrollY) {
        progress = scrolledInWrapper / _cachedScrollableDistance;
        if (progress < 0) progress = 0;
        else if (progress > 1) progress = 1;
        isInStickyRange = true;
        hasCompletedSection = progress >= 1.0;
    } else {
        progress = 1.0;
        hasCompletedSection = true;
    }

    _cachedScrollMetrics.progress = progress;
    _cachedScrollMetrics.scrollInSection = scrolledInWrapper;
    _cachedScrollMetrics.inFinalDezoom = progress >= 0.90;
    _cachedScrollMetrics.isInStickyRange = isInStickyRange;
    _cachedScrollMetrics.hasCompletedSection = hasCompletedSection;
    _cachedScrollMetrics.wrapperHeight = _cachedWrapperHeight;
    return _cachedScrollMetrics;
}

// --- SHADERS ---
const mountainVertexShader = `
    uniform float uTime;
    uniform float uSize;
    uniform float uNoiseStrength;
    varying vec3 vPos;
    // Remplace l'ancien smoothNoise (8 hash + 7 mix / point) par 3 sin + 1 pseudo-hash. Gain GPU massif.
    void main() {
        vec3 transformed = position;
        float t = uTime;
        float s = uNoiseStrength;
        transformed.x += sin(t * 0.30 + position.z * 0.020 + position.y * 0.013) * s;
        transformed.y += cos(t * 0.20 + position.x * 0.010 + position.z * 0.017) * s;
        transformed.z += sin(t * 0.30 + position.y * 0.030 + position.x * 0.011) * s;
        float jitterSeed = fract(sin(dot(position, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
        transformed.xyz += (jitterSeed - 0.5) * s * 1.5;
        vPos = transformed;
        vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        gl_PointSize = uSize * (300.0 / -mvPosition.z);
    }
`;

const mountainFragmentShader = `
    uniform vec3 uColor;
    uniform vec4 uMapBounds; 
    uniform float uFadeDist; 
    varying vec3 vPos;
    void main() {
        float distToCenter = distance(gl_PointCoord, vec2(0.5));
        if (distToCenter > 0.5) discard;
        float alpha = 1.0 - smoothstep(0.0, 0.5, distToCenter);
        float heightFade = smoothstep(-150.0, 50.0, vPos.y);
        alpha *= heightFade;
        float fadeXLeft = smoothstep(uMapBounds.x, uMapBounds.x + uFadeDist, vPos.x);
        float fadeXRight = smoothstep(uMapBounds.y, uMapBounds.y - uFadeDist, vPos.x);
        float fadeZBack = smoothstep(uMapBounds.z, uMapBounds.z + uFadeDist, vPos.z);
        float fadeZFront = smoothstep(uMapBounds.w, uMapBounds.w - uFadeDist, vPos.z);
        alpha *= fadeXLeft * fadeXRight * fadeZBack * fadeZFront;
        alpha *= 0.6; 
        gl_FragColor = vec4(uColor, alpha);
    }
`;

function parseHTMLData() {
    // Steps = pourcentages (10, 50, 90) → 10%, 50%, 90% sur la timeline
    CONFIG.timeline.startYear = 0;
    CONFIG.timeline.endYear = 100;

    const dataContainer = document.getElementById('mm-fidelity-timeline-data');
    if (dataContainer) {
        try {
            const rawData = JSON.parse(dataContainer.textContent);
            if (Array.isArray(rawData) && rawData.length > 0) {
                const steps = [];
                rawData.forEach(item => {
                    const date = parseInt(item.date, 10);
                    if (!isNaN(date) && item.id && date >= 0 && date <= 100) {
                        steps.push({
                            id: item.id,
                            date: date,
                            title: item.title || "",
                            color: item.color || CONFIG.hexColor
                        });
                    }
                });
                CONFIG.timeline.steps = steps.sort((a, b) => a.date - b.date);
                // Après avoir trié, on récupère les couleurs sur les cartes si besoin
                CONFIG.timeline.steps.forEach(step => {
                    const card = document.querySelector(`.loyalty-card[data-status="${step.id}"]`);
                    if (card) {
                        const style = window.getComputedStyle(card);
                        const tierColor = style.getPropertyValue('--tier-color').trim();
                        if (tierColor) step.color = tierColor;
                    }
                });
                return;
            }
        } catch (e) {
            console.error("❌ MM Fidelity: Erreur parsing JSON", e);
        }
    }

    // Fallback : 3 blocs 10%, 50%, 90% depuis les cartes (bronze, silver, gold)
    const cards = document.querySelectorAll('.loyalty-card');
    const defaultSteps = [
        { id: 'bronze', date: 10 },
        { id: 'silver', date: 50 },
        { id: 'gold', date: 90 }
    ];
    const steps = [];
    cards.forEach(card => {
        const status = card.dataset.status;
        const step = defaultSteps.find(s => s.id === status);
        if (step) steps.push(step);
    });
    CONFIG.timeline.steps = steps.sort((a, b) => a.date - b.date);
}

function init() {
    parseHTMLData();
    document.documentElement.style.setProperty('--active-color', CONFIG.hexColor);

    createTimelineDOM();
    createContentDOM();
    initTimelineNavArrow();

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    scene.fog = new THREE.FogExp2(0xffffff, 0.0015);

    clock = new THREE.Clock();

    _cachedWrapper = document.getElementById('mm-fidelity-wrapper');
    _cachedRightPanel = document.querySelector('#mm-fidelity-wrapper .right-panel');
    _cachedCanvas = document.getElementById('mm-fidelity-canvas');
    _cachedNavArrow = document.getElementById('mm-fidelity-timeline-nav-arrow');
    refreshFidelityLayout();

    const panelWidth = _cachedRightPanel ? _cachedRightPanel.clientWidth : window.innerWidth;
    const panelHeight = _cachedRightPanel ? _cachedRightPanel.clientHeight : window.innerHeight;

    camera = new THREE.PerspectiveCamera(45, panelWidth / panelHeight, 0.01, 10000);
    camera.position.set(0, 50, 100);

    if (!_cachedCanvas) {
        console.error('❌ MM Fidelity: Canvas introuvable');
        return;
    }

    try {
        renderer = new THREE.WebGLRenderer({
            canvas: _cachedCanvas,
            antialias: QUALITY.antialias,
            alpha: true,
            powerPreference: "high-performance"
        });
    } catch (e) {
        console.error('❌ MM Fidelity: WebGL initialization failed', e);
        initialized = false;
        return;
    }

    if (!renderer) {
        console.error('❌ MM Fidelity: Renderer could not be created');
        initialized = false;
        return;
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, QUALITY.pixelRatioCap));
    renderer.setSize(panelWidth, panelHeight);
    renderer.transparent = true;
    // Shadow map désactivée : très coûteuse (PCFSoft + 1024x1024) pour quelques shields.
    renderer.shadowMap.enabled = QUALITY.shadowsEnabled;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.65);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.1);
    dirLight.position.set(40, 90, 40);
    dirLight.castShadow = QUALITY.shadowsEnabled;
    scene.add(dirLight);

    loadModels();
    initOrbitControls();

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('scroll', onScroll);

    // Pause totale quand la tab n'est plus visible
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            isAnimating = false;
        } else if (_cachedWrapper) {
            const rect = _cachedWrapper.getBoundingClientRect();
            const inView = rect.bottom > 0 && rect.top < window.innerHeight;
            if (inView && !isAnimating) {
                isAnimating = true;
                _forceRenderFrames = 3;
                requestAnimationFrame(animate);
            }
        }
    });

    isAnimating = true;
    animate();
}

function createTimelineDOM() {
    let container = document.getElementById('mm-fidelity-timeline-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'mm-fidelity-timeline-container';
        const stickyView = document.getElementById('mm-fidelity-sticky-view');
        if (stickyView) stickyView.appendChild(container);
        else document.body.appendChild(container);
    }

    timelineStrip = document.getElementById('mm-fidelity-timeline-strip');
    if (!timelineStrip) {
        timelineStrip = document.createElement('div');
        timelineStrip.id = 'mm-fidelity-timeline-strip';
        container.appendChild(timelineStrip);
    }

    let marker = document.getElementById('mm-fidelity-timeline-marker');
    if (!marker) {
        marker = document.createElement('div');
        marker.id = 'mm-fidelity-timeline-marker';
        const stickyView = document.getElementById('mm-fidelity-sticky-view');
        if (stickyView) stickyView.appendChild(marker);
        else document.body.appendChild(marker);
    }

    const { startYear, endYear, tickSpacing, steps } = CONFIG.timeline;
    const targetYears = steps.map(s => s.date);

    // Calculer combien de ticks sont nécessaires pour remplir la moitié de l'écran
    // On ajoute une marge de sécurité (* 1.5)
    const ticksNeeded = Math.ceil((window.innerWidth / 2) / tickSpacing) + 10;

    const displayStart = startYear - ticksNeeded;
    const displayEnd = endYear + ticksNeeded;

    for (let year = displayStart; year <= displayEnd; year++) {
        const xPos = (year - startYear) * tickSpacing;
        const wrapper = document.createElement('div');
        wrapper.className = 't-tick-wrapper';
        wrapper.style.left = `${xPos}px`;

        const isStep = targetYears.includes(year);
        if (isStep) wrapper.classList.add('t-step');

        const label = document.createElement('div');
        label.className = 't-label';
        
        // Afficher le nom de l'étape au lieu du numéro
        const stepData = steps.find(s => s.date === year);
        if (stepData) {
            // Capitaliser le nom (bronze -> Bronze)
            label.innerText = stepData.id.charAt(0).toUpperCase() + stepData.id.slice(1);
        } else {
            label.innerText = '';
        }
        wrapper.appendChild(label);

        const line = document.createElement('div');
        line.className = 't-line';
        wrapper.appendChild(line);

        wrapper.dataset.year = year;
        timelineStrip.appendChild(wrapper);
        allLabels.push({ year, el: wrapper, isStep });
    }
}

function createContentDOM() {
    // Content is now hardcoded in the Liquid template
    allCards = Array.from(document.querySelectorAll('.loyalty-card')).map(el => ({
        id: el.dataset.status,
        el: el
    }));
    
    // Masquer toutes les cartes au départ
    allCards.forEach((cardObj) => {
        cardObj.el.style.opacity = '0';
        cardObj.el.style.visibility = 'hidden';
    });
}

// --- LOGIQUE THREE.JS ---

async function loadModels() {
    const loader = new GLTFLoader();
    const wrapper = document.getElementById('mm-fidelity-wrapper');
    if (!wrapper) {
        console.error('❌ MM Fidelity: Wrapper introuvable');
        return;
    }
    
    const mountainModelUrl = wrapper.dataset.mountainModel;
    const lineModelUrl = wrapper.dataset.lineModel;
    
    if (!mountainModelUrl || !lineModelUrl) {
        console.error('❌ MM Fidelity: URLs des modèles 3D manquantes');
        return;
    }
    
    try {
        const [mountainGltf, lineGltf] = await Promise.all([
            loader.loadAsync(mountainModelUrl),
            loader.loadAsync(lineModelUrl)
        ]);

        const mountainRoot = mountainGltf.scene;
        mountainRoot.updateMatrixWorld(true);
        let mountainMesh = null;
        mountainRoot.traverse((node) => {
            if (node.isMesh && (!mountainMesh || node.geometry.attributes.position.count > mountainMesh.geometry.attributes.position.count)) {
                mountainMesh = node;
            }
        });
        if (mountainMesh) setupMountainPoints(mountainMesh);

        const lineRoot = lineGltf.scene;
        lineRoot.updateMatrixWorld(true);
        let lineObj = null;
        lineRoot.traverse((node) => {
            if (!node.geometry || !node.geometry.attributes.position) return;
            const isLineLike = node.isLine || node.isLineSegments || (node.isMesh && node.geometry.attributes.position.count >= 2);
            if (isLineLike && (!lineObj || node.geometry.attributes.position.count > (lineObj.geometry?.attributes?.position?.count || 0))) {
                lineObj = node;
            }
        });
        if (lineObj) {
            setupPath(lineObj);
        } else {
            console.error('❌ MM Fidelity: Aucun objet line trouvé dans le modèle GLB');
        }

    } catch (error) {
        console.error('❌ MM Fidelity: Erreur chargement modèles', error);
    }
}

function setupMountainPoints(mesh) {
    const geometry = mesh.geometry;
    const positions = geometry.attributes.position;
    const count = positions.count;
    const maxPoints = QUALITY.maxPoints;
    const step = Math.max(1, Math.floor(count / maxPoints));
    const sampledPositions = [];
    const tempVec = new THREE.Vector3();
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;

    for (let i = 0; i < count; i += step) {
        tempVec.set(positions.getX(i), positions.getY(i), positions.getZ(i));
        tempVec.applyMatrix4(mesh.matrixWorld);
        sampledPositions.push(tempVec.x, tempVec.y, tempVec.z);
        minX = Math.min(minX, tempVec.x);
        maxX = Math.max(maxX, tempVec.x);
        minZ = Math.min(minZ, tempVec.z);
        maxZ = Math.max(maxZ, tempVec.z);
    }
    const pointsGeometry = new THREE.BufferGeometry();
    pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(sampledPositions, 3));
    const material = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uSize: { value: getPointSize() },
            uColor: { value: CONFIG.colorVector },
            uNoiseStrength: { value: CONFIG.noiseStrength },
            uMapBounds: { value: new THREE.Vector4(minX, maxX, minZ, maxZ) },
            uFadeDist: { value: CONFIG.edgeFadeDistance }
        },
        vertexShader: mountainVertexShader,
        fragmentShader: mountainFragmentShader,
        transparent: true,
        depthWrite: false,
    });
    mountainPoints = new THREE.Points(pointsGeometry, material);
    scene.add(mountainPoints);
}

function setupPath(lineObj) {
    const positionsAttr = lineObj.geometry.attributes.position;
    const points = [];
    const tempVec = new THREE.Vector3();
    for (let i = 0; i < positionsAttr.count; i++) {
        tempVec.set(positionsAttr.getX(i), positionsAttr.getY(i), positionsAttr.getZ(i));
        tempVec.applyMatrix4(lineObj.matrixWorld);
        points.push(tempVec.clone());
    }
    if (points.length < 2) {
        console.error('❌ MM Fidelity: Pas assez de points dans le modèle line');
        return;
    }
    points.reverse();
    pathCurve = new THREE.CatmullRomCurve3(points);
    const startTangent = pathCurve.getTangentAt(0).normalize();
    lastStableDirection.copy(startTangent);
    lastStableLookAt.copy(pathCurve.getPointAt(0.1));
    const realLength = pathCurve.getLength();
    totalLineLength = realLength * 1.05;
    const linePositions = [];
    const curvePoints = pathCurve.getSpacedPoints(CONFIG.lineDensity);
    curvePoints.forEach(p => {
        linePositions.push(p.x, p.y, p.z);
    });
    const geometry = new LineGeometry();
    geometry.setPositions(linePositions);
    const size = new THREE.Vector2();
    renderer.getSize(size);
    const material = new LineMaterial({
        color: 0x0055ff,
        linewidth: getLineWidth(),
        resolution: size,
        dashed: true,
        dashScale: 1,
        dashSize: totalLineLength,
        gapSize: totalLineLength * 2,
        alphaToCoverage: false,
        worldUnits: false,
        depthTest: false,
        transparent: true,
        opacity: 1.0
    });
    pathLine = new Line2(geometry, material);
    pathLine.computeLineDistances();
    pathLine.renderOrder = 999;
    pathLine.frustumCulled = false;
    pathLine.material.dashOffset = totalLineLength;
    scene.add(pathLine);
    pathLine.geometry.instanceCount = CONFIG.lineDensity;
    if (points.length > 0) {
        const startPos = points[0].clone();
        const camOff = getCamOffset();
        startPos.y += camOff.up;
        startPos.z += camOff.back;
        camera.position.copy(startPos);
    }
    setupDynamicSteps();
}

function buildStepIconResources() {
    if (stepIconGeometry && stepIconMaterial && stepShadowGeometry) return;

    const svgLoader = new SVGLoader();
    const svgData = svgLoader.parse(STEP_SVG);
    const shapes = [];
    svgData.paths.forEach(path => {
        const pathShapes = SVGLoader.createShapes(path);
        pathShapes.forEach(shape => shapes.push(shape));
    });

    const extrudeSettings = {
        depth: CONFIG.stepExtrudeDepth,
        bevelEnabled: true,
        bevelThickness: CONFIG.stepExtrudeDepth * 0.35,
        bevelSize: CONFIG.stepExtrudeDepth * 0.25,
        bevelSegments: QUALITY.iconBevelSegments,
        curveSegments: QUALITY.iconCurveSegments
    };

    stepIconGeometry = new THREE.ExtrudeGeometry(shapes, extrudeSettings);
    stepIconGeometry.computeBoundingBox();
    if (stepIconGeometry.boundingBox) {
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        stepIconGeometry.boundingBox.getSize(size);
        stepIconGeometry.boundingBox.getCenter(center);
        stepIconGeometry.translate(-center.x, -center.y, -center.z);
        const scaleFactor = CONFIG.stepIconSize / Math.max(size.x, size.y);
        stepIconGeometry.scale(scaleFactor, scaleFactor, scaleFactor);
        stepIconGeometry.scale(1, -1, 1);
    }

    // MeshLambertMaterial au lieu de MeshStandardMaterial : PBR + metalness/roughness
    // ne sont pas nécessaires ici (les shields bougent, le glow vient de emissive).
    // Fragment shader beaucoup plus cheap, rendu très proche visuellement.
    stepIconMaterial = new THREE.MeshLambertMaterial({
        color: new THREE.Color(0xd9d9d9),
        emissive: new THREE.Color(0x000000),
        emissiveIntensity: 1.0,
        transparent: true,
        depthTest: false,
        depthWrite: false
    });
    stepIconMaterial.toneMapped = false;

    // Idem pour la version "active" : on garde juste l'émissive pour le glow
    stepIconMaterialActive = new THREE.MeshLambertMaterial({
        color: new THREE.Color(CONFIG.hexColor),
        emissive: new THREE.Color(CONFIG.hexColor),
        emissiveIntensity: CONFIG.stepShineIntensity + 0.35,
        transparent: true,
        depthTest: false
    });
    stepIconMaterialActive.toneMapped = false;

    stepShadowGeometry = new THREE.CircleGeometry(CONFIG.stepShadowSize, QUALITY.circleSegments);
}

function setupDynamicSteps() {
    if (!pathCurve) return;
    stepMeshes.forEach(mesh => scene.remove(mesh));
    stepMeshes = [];
    const { startYear, endYear, steps } = CONFIG.timeline;
    const totalDuration = endYear - startYear;
    buildStepIconResources();

    steps.forEach(stepConfig => {
        const date = stepConfig.date;
        if (date < startYear || date > endYear) return;
        const progressPos = (date - startYear) / totalDuration;
        const point3D = pathCurve.getPointAt(progressPos);
        const group = new THREE.Group();
        group.position.copy(point3D);
        group.renderOrder = 1200;
        group.userData.progressPos = progressPos;
        group.userData.floatPhase = Math.random() * Math.PI * 2;
        group.userData.wasActive = false;
        group.userData.pulse = 0;

        const iconMaterial = stepIconMaterial.clone();
        const iconMesh = new THREE.Mesh(stepIconGeometry, iconMaterial);
        iconMesh.castShadow = QUALITY.shadowsEnabled;
        iconMesh.position.y = CONFIG.stepFloatBase;
        iconMesh.rotation.y = Math.PI;
        iconMesh.renderOrder = 1210;

        // Ajout d'une bordure noire
        const borderMaterial = new THREE.MeshBasicMaterial({ 
            color: 0x000000, 
            side: THREE.BackSide,
            transparent: true,
            opacity: 0.9,
            depthTest: false
        });
        const borderMesh = new THREE.Mesh(stepIconGeometry, borderMaterial);
        borderMesh.scale.set(1.1, 1.1, 1.1); // Légèrement plus grand
        borderMesh.renderOrder = 1205; // Juste derrière l'icône
        iconMesh.add(borderMesh);

        group.add(iconMesh);

        const shadowMaterial = new THREE.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: CONFIG.stepShadowOpacity,
            depthWrite: false
        });
        const shadowMesh = new THREE.Mesh(stepShadowGeometry, shadowMaterial);
        shadowMesh.rotation.x = -Math.PI / 2;
        shadowMesh.position.y = 0.02;
        shadowMesh.renderOrder = 1190;
        group.add(shadowMesh);

        group.userData.iconMesh = iconMesh;
        group.userData.iconMaterial = iconMaterial;
        group.userData.shadowMesh = shadowMesh;
        group.userData.stepColor = stepConfig.color || CONFIG.hexColor;
        // Pré-calcule la couleur du palier (évite un new THREE.Color chaque frame).
        group.userData.stepColorObj = new THREE.Color(group.userData.stepColor);
        // Applique une fois la couleur de base (elle ne change plus).
        iconMaterial.color.copy(group.userData.stepColorObj);

        group.scale.set(0.001, 0.001, 0.001);
        scene.add(group);
        stepMeshes.push(group);
    });
    updateScrollAnchors();
}

function updateCardsVisibilityForStep(activeIndex, easedProgress) {
    const effectiveIndex = Math.max(0, activeIndex);
    if (effectiveIndex === currentActiveCardIndex) return;

    allCards.forEach((cardObj, index) => {
        const isActive = index === effectiveIndex;
        
        if (isActive) {
            cardObj.el.classList.add('active');
            
            // Sur mobile, on laisse le CSS gérer les transitions simples (pas de GSAP complexe)
            if (!isMobile() && typeof gsap !== 'undefined') {
                const tl = gsap.timeline({ overwrite: true });
                tl.fromTo(cardObj.el, 
                    { autoAlpha: 0, y: 80, scale: 0.92, filter: 'blur(10px)' }, 
                    { autoAlpha: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 1.2, ease: 'expo.out' }
                );

                const children = cardObj.el.querySelectorAll('.card-header, .shipping-item, .benefits-list li');
                tl.fromTo(children,
                    { autoAlpha: 0, y: 20 },
                    { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.08, ease: 'power3.out' },
                    '-=0.8'
                );
            } else if (!isMobile()) {
                // Fallback non-GSAP desktop uniquement
                cardObj.el.style.opacity = '1';
                cardObj.el.style.visibility = 'visible';
                cardObj.el.style.transform = 'translateY(0) scale(1)';
            }
        } else {
            cardObj.el.classList.remove('active');
            
            if (!isMobile() && typeof gsap !== 'undefined') {
                gsap.to(cardObj.el, { 
                    autoAlpha: 0, y: -40, scale: 0.96, filter: 'blur(5px)',
                    duration: 0.6, ease: 'power2.in', overwrite: true 
                });
            } else if (!isMobile()) {
                // Fallback non-GSAP desktop uniquement
                cardObj.el.style.opacity = '0';
                cardObj.el.style.visibility = 'hidden';
                cardObj.el.style.transform = 'translateY(-40px) scale(0.96)';
            }
        }
    });
    
    currentActiveCardIndex = effectiveIndex;
}

function onScroll() {
    _lastScrollActivityAt = performance.now();
    _forceRenderFrames = 2;
    // updateTimelineNavArrow est appelé dans animate(), pas besoin de le dupliquer ici.
}

function initTimelineNavArrow() {
    const arrow = document.getElementById('mm-fidelity-timeline-nav-arrow');
    if (!arrow) return;

    arrow.addEventListener('click', () => {
        const scrollMetrics = getScrollProgress();
        if (!scrollMetrics || scrollMetrics.wrapperHeight <= window.innerHeight) return;

        // Récupérer le sticky top du right-panel
        const rightPanel = document.querySelector('#mm-fidelity-wrapper .right-panel');
        let stickyTop = 0;
        if (rightPanel) {
            const topStr = window.getComputedStyle(rightPanel).top;
            const parsed = parseFloat(topStr);
            if (!Number.isNaN(parsed)) stickyTop = parsed;
        }

        const wrapper = document.getElementById('mm-fidelity-wrapper');
        if (!wrapper) return;

        const wrapperTopInDocument = wrapper.getBoundingClientRect().top + window.scrollY;
        const startScrollY = wrapperTopInDocument - stickyTop;
        const scrollableDistance = scrollMetrics.wrapperHeight - window.innerHeight;
        
        if (scrollableDistance <= 0) return;

        // Utiliser le progress RÉEL (basé sur scrollY) pour éviter les effets d'inertie
        const currentProgress = Math.max(0, Math.min(1, scrollMetrics.progress));
        
        // 0-50% : aller à la fin (100%)
        // 50-100% : aller au début (0%)
        const goToEnd = currentProgress < 0.5;
        const targetProgressValue = goToEnd ? 0.999 : 0.001;
        
        // Calculer la position de scroll réelle nécessaire (avec stickyTop)
        const targetScrollTop = startScrollY + targetProgressValue * scrollableDistance;
        
        // Faire le scroll réel de la page (smooth)
        window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    });
}

function updateTimelineNavArrow() {
    if (_cachedNavArrow === null) {
        _cachedNavArrow = document.getElementById('mm-fidelity-timeline-nav-arrow') || false;
    }
    const arrow = _cachedNavArrow;
    if (!arrow) return;

    const scrollMetrics = _cachedScrollMetrics;
    if (!scrollMetrics.wrapperHeight || scrollMetrics.wrapperHeight <= window.innerHeight) {
        if (arrow.style.display !== 'none') arrow.style.display = 'none';
        return;
    }

    if (arrow.style.display !== 'flex') arrow.style.display = 'flex';

    const isDown = scrollMetrics.progress < 0.5;
    if (isDown !== _lastArrowIsDown) {
        _lastArrowIsDown = isDown;
        if (isDown) {
            arrow.classList.remove('arrow-up');
            arrow.classList.add('arrow-down');
            arrow.setAttribute('aria-label', 'Aller à la fin');
        } else {
            arrow.classList.remove('arrow-down');
            arrow.classList.add('arrow-up');
            arrow.setAttribute('aria-label', 'Aller au début');
        }
    }
}

let _fidResizeTimer = null;
function onWindowResize() {
    clearTimeout(_fidResizeTimer);
    _fidResizeTimer = setTimeout(() => {
        if (!camera || !renderer) return;
        const panelWidth = _cachedRightPanel ? _cachedRightPanel.clientWidth : window.innerWidth;
        const panelHeight = _cachedRightPanel ? _cachedRightPanel.clientHeight : window.innerHeight;
        camera.aspect = panelWidth / panelHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(panelWidth, panelHeight);
        if (pathLine) {
            pathLine.material.resolution.set(panelWidth, panelHeight);
            pathLine.material.linewidth = getLineWidth();
        }
        if (mountainPoints && mountainPoints.material.uniforms) {
            mountainPoints.material.uniforms.uSize.value = getPointSize();
        }
        refreshFidelityLayout();
        _forceRenderFrames = 3;
    }, 150);
}

function updateScrollAnchors() {
    // Plus utilisé pour le progress (calcul direct via getScrollProgress)
    scrollAnchors = [];
}

/**
 * --- ORBIT CONTROLS ---
 */
function initOrbitControls() {
    const canvas = document.getElementById('mm-fidelity-canvas');
    if (!canvas) return;

    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerLeave);
    
    // Parallaxe (Gyroscope) sur TOUTE la section
    const wrapper = document.getElementById('mm-fidelity-wrapper');
    if (wrapper) {
        wrapper.addEventListener('mousemove', onMouseMove);
        wrapper.addEventListener('mouseleave', onMouseLeave);
    }
}

let _fidMouseMoveTime = 0;
function onMouseMove(event) {
    const now = performance.now();
    if (now - _fidMouseMoveTime < 16) return; // Throttle ~60fps
    _fidMouseMoveTime = now;
    if (!_cachedWrapper) return;
    const rect = _cachedWrapper.getBoundingClientRect();
    targetMouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    targetMouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    _forceRenderFrames = 2;
}

function onMouseLeave() {
    targetMouseX = 0;
    targetMouseY = 0;
    isDragging = false; 
}

function onPointerDown(event) {
    if (currentEasedProgress < CONFIG.orbitEnabledFromProgress) return;
    
    isDragging = true;
    prevPointerX = event.clientX;
    prevPointerY = event.clientY;
    pointerDownX = event.clientX;
    pointerDownY = event.clientY;
    
    const canvas = document.getElementById('mm-fidelity-canvas');
    if (canvas) canvas.style.cursor = 'grabbing';
}

function onPointerMove(event) {
    const canvas = document.getElementById('mm-fidelity-canvas');
    if (isDragging) {
        const deltaX = event.clientX - prevPointerX;
        const deltaY = event.clientY - prevPointerY;
        
        targetOrbitAngle -= deltaX * CONFIG.orbitSensitivity * 0.01;
        targetOrbitPitch -= deltaY * CONFIG.orbitSensitivity * 0.01;
        
        // Appliquer les limites
        targetOrbitAngle = Math.max(-CONFIG.orbitAngleMax, Math.min(CONFIG.orbitAngleMax, targetOrbitAngle));
        targetOrbitPitch = Math.max(CONFIG.orbitPitchMin, Math.min(CONFIG.orbitPitchMax, targetOrbitPitch));
        
        prevPointerX = event.clientX;
        prevPointerY = event.clientY;
    } else if (canvas) {
        const orbitAllowed = currentEasedProgress >= CONFIG.orbitEnabledFromProgress;
        canvas.style.cursor = orbitAllowed ? 'grab' : 'default';
    }
}

function onPointerUp() {
    isDragging = false;
    const canvas = document.getElementById('mm-fidelity-canvas');
    if (canvas) {
        const orbitAllowed = currentEasedProgress >= CONFIG.orbitEnabledFromProgress;
        canvas.style.cursor = orbitAllowed ? 'grab' : 'default';
    }
}

function onPointerLeave() {
    if (isDragging) return; // Continuer le drag même si on sort
}

function animate(time) {
    if (!isAnimating) return;

    if (!renderer || !scene || !camera) {
        console.error('⚠️ MM Fidelity: Animation interrompue car le Renderer/Scene/Camera est manquant');
        isAnimating = false;
        return;
    }

    requestAnimationFrame(animate);

    const deltaTime = clock.getDelta();
    const elapsedTime = clock.getElapsedTime();

    // --- PROGRESS (1 seul getScrollProgress / frame) ---
    const scrollMetrics = getScrollProgress();
    const { progress: scrollProgressRaw, isInStickyRange, hasCompletedSection } = scrollMetrics;

    if (hasCompletedSection) {
        targetProgress = 1.0;
    } else if (isInStickyRange) {
        targetProgress = scrollProgressRaw;
    } else {
        targetProgress = 0;
    }

    const prevScroll = scrollProgress;
    scrollProgress += (targetProgress - scrollProgress) * CONFIG.scrollSpeed;
    const rawProgress = Math.max(0, Math.min(1, scrollProgress));
    const easedProgress = easeInOutCubic(rawProgress);
    currentEasedProgress = easedProgress;

    // --- MOUNTAIN TIME (frozen quand idle) ---
    const now = performance.now();
    const scrollDelta = Math.abs(targetProgress - prevScroll);
    const isUserActive =
        scrollDelta > 0.0005 ||
        isInStickyRange ||
        (now - _lastScrollActivityAt) < QUALITY.idleFreezeMs;
    if (isUserActive) {
        _mountainTime += deltaTime;
    }
    if (mountainPoints && mountainPoints.material.uniforms) {
        mountainPoints.material.uniforms.uTime.value = _mountainTime;
    }

    // --- CARDS ACTIVE INDEX ---
    const { steps } = CONFIG.timeline;
    let activeCardIndex = 0;
    for (let i = 0; i < steps.length; i++) {
        if (easedProgress >= steps[i].date / 100) activeCardIndex = i;
    }
    updateCardsVisibilityForStep(activeCardIndex, easedProgress);

    // --- TIMELINE DOM (throttlé) ---
    if (timelineStrip) {
        const { startYear, endYear, tickSpacing } = CONFIG.timeline;
        const totalYears = endYear - startYear;
        const currentYearFloat = startYear + (easedProgress * totalYears);

        const yearDelta = Math.abs(currentYearFloat - _lastUIYearFloat);
        const timelineNeedsUpdate =
            Number.isNaN(_lastUIYearFloat) || yearDelta > 0.08 || activeCardIndex !== _lastActiveLabelIndex;

        if (timelineNeedsUpdate) {
            _lastUIYearFloat = currentYearFloat;

            const xOffset = -(currentYearFloat - startYear) * tickSpacing;
            const xRounded = Math.round(xOffset * 100) / 100;
            if (xRounded !== _lastTimelineXOffset) {
                _lastTimelineXOffset = xRounded;
                timelineStrip.style.transform = `translateX(${xRounded}px)`;
            }

            // Label actif : ne toucher classList / styles que quand l'index change réellement
            if (activeCardIndex !== _lastActiveLabelIndex) {
                _lastActiveLabelIndex = activeCardIndex;
                const stepData = steps[activeCardIndex];
                const activeColor = stepData ? (stepData.color || CONFIG.hexColor) : CONFIG.hexColor;
                for (let i = 0; i < allLabels.length; i++) {
                    const item = allLabels[i];
                    if (!item.isStep) continue;
                    const shouldBeActive = !!stepData && item.year === stepData.date;
                    if (shouldBeActive !== !!item._active) {
                        item._active = shouldBeActive;
                        item.el.classList.toggle('t-active', shouldBeActive);
                        if (shouldBeActive) {
                            item.el.style.setProperty('--active-tier-color', activeColor);
                        } else {
                            item.el.style.removeProperty('--active-tier-color');
                        }
                    } else if (shouldBeActive) {
                        item.el.style.setProperty('--active-tier-color', activeColor);
                    }
                }
            }
        }
    }

    // --- WEBGL UPDATE ---
    let anyStepAnimating = false;
    if (pathLine && pathCurve) {
        if (pathLine.material) {
            const shouldShowLine = rawProgress > 0.001 || hasCompletedSection;
            const opacityTarget = shouldShowLine ? 1.0 : 0.0;
            if (pathLine.material.opacity !== opacityTarget) pathLine.material.opacity = opacityTarget;
            pathLine.material.dashOffset = hasCompletedSection ? 0 : (totalLineLength * (1 - easedProgress));
        }

        for (let i = 0; i < stepMeshes.length; i++) {
            const group = stepMeshes[i];
            const ud = group.userData;
            const isActive = i <= activeCardIndex;
            if (isActive && !ud.wasActive) {
                ud.pulse = 1;
            }
            ud.wasActive = isActive;

            if (ud.pulse > 0) {
                ud.pulse = Math.max(0, ud.pulse - deltaTime * CONFIG.stepPulseDecay);
                anyStepAnimating = true;
            }
            if (isActive) anyStepAnimating = true;

            const pulseScale = ud.pulse * CONFIG.stepPulseScale;
            const targetScale = isActive ? CONFIG.stepActiveScale + pulseScale : CONFIG.stepBaseScale;
            _fvScale.set(targetScale, targetScale, targetScale);
            group.scale.lerp(_fvScale, 0.08);

            const iconMesh = ud.iconMesh;
            const iconMaterial = ud.iconMaterial;
            const shadowMesh = ud.shadowMesh;
            if (!iconMesh || !shadowMesh) continue;

            if (iconMaterial) {
                // La couleur de base a été appliquée une fois à la création.
                // On ne change que l'émissive (glow) selon l'état.
                if (isActive) {
                    if (iconMaterial.emissiveIntensity !== 2.5) {
                        iconMaterial.emissive.copy(ud.stepColorObj);
                        iconMaterial.emissiveIntensity = 2.5;
                    }
                } else {
                    if (iconMaterial.emissiveIntensity !== 0) {
                        iconMaterial.emissive.setHex(0x000000);
                        iconMaterial.emissiveIntensity = 0;
                    }
                }
            }

            // Animation de flottement — uniquement quand l'utilisateur est actif,
            // sinon on garde la dernière valeur (fige visuel, économise des sin/cos).
            if (isUserActive) {
                const floatOffset = Math.sin(elapsedTime * CONFIG.stepFloatSpeed + ud.floatPhase) * CONFIG.stepFloatAmplitude;
                iconMesh.position.y = CONFIG.stepFloatBase + floatOffset;
                iconMesh.rotation.y = elapsedTime * CONFIG.stepRotationSpeed + ud.floatPhase;
                iconMesh.rotation.x = Math.sin(elapsedTime * 0.5 + ud.floatPhase) * 0.15;

                const heightFactor = (floatOffset / CONFIG.stepFloatAmplitude + 1) * 0.5;
                const shadowScale = 1 - heightFactor * 0.25;
                shadowMesh.scale.set(shadowScale, shadowScale, shadowScale);
                shadowMesh.material.opacity = CONFIG.stepShadowOpacity * (1 - heightFactor * 0.4);
                anyStepAnimating = true;
            }

            if (iconMaterial) {
                if (isActive) {
                    iconMaterial.opacity = 1;
                } else {
                    // Distance caméra → icon, sans allocation Vector3
                    iconMesh.getWorldPosition(_fv1);
                    const dist = camera.position.distanceTo(_fv1);
                    const range = CONFIG.stepFadeFar - CONFIG.stepFadeNear;
                    let t = (dist - CONFIG.stepFadeNear) / range;
                    if (t < 0) t = 0; else if (t > 1) t = 1;
                    iconMaterial.opacity = 1 - t;
                }
            }
        }

        // --- CAMERA PATH ---
        // Utilise des vecteurs réutilisables — 0 alloc / frame ici.
        pathCurve.getPointAt(easedProgress, _fv1); // currentPoint

        // Snap sur un step si proche
        let targetPoint = _fv1;
        for (let i = 0; i < steps.length; i++) {
            const stepProgress = steps[i].date / 100;
            if (Math.abs(easedProgress - stepProgress) < 0.005) {
                pathCurve.getPointAt(stepProgress, _fv2);
                targetPoint = _fv2;
                break;
            }
        }

        const LOCK_THRESHOLD = 0.90;
        let smoothedDirection, lookAtTarget;
        if (easedProgress < LOCK_THRESHOLD) {
            const lookAheadStep = Math.min(0.999, easedProgress + CONFIG.lookAhead);
            pathCurve.getPointAt(lookAheadStep, _fv3); // futurePoint
            _fv4.subVectors(_fv3, targetPoint).normalize(); // smoothedDirection
            smoothedDirection = _fv4;
            lookAtTarget = _fv3;
            lookAtTarget.y += CONFIG.lookAtOffsetY;
            lastStableDirection.copy(smoothedDirection);
            lastStableLookAt.copy(lookAtTarget);
        } else {
            smoothedDirection = lastStableDirection;
            lookAtTarget = lastStableLookAt;
        }

        const camOff = getCamOffset();

        // desiredPosition (dans _fv5)
        _fv5.copy(smoothedDirection).multiplyScalar(-camOff.back);
        _fv5.y = 0;

        let extraDistance = 0;
        if (!isMobile()) {
            if (easedProgress <= 0.01) {
                extraDistance = getInitialExtraDistance() * 1.5;
            } else if (easedProgress < 0.90) {
                const normalizedProgress = (easedProgress - 0.01) / 0.89;
                const zoomFactor = Math.pow(1 - normalizedProgress, CONFIG.zoomEasePower);
                extraDistance = zoomFactor * getInitialExtraDistance();
            } else {
                const finalDezoomProgress = (easedProgress - 0.90) / 0.10;
                const baseZoom = Math.pow(1 - 0.90, CONFIG.zoomEasePower) * getInitialExtraDistance();
                extraDistance = baseZoom + (CONFIG.finalExtraDistance * finalDezoomProgress);
            }
        }
        _fv5.addScaledVector(smoothedDirection, -extraDistance);
        _fv5.x += targetPoint.x;
        _fv5.y += targetPoint.y + camOff.up;
        _fv5.z += targetPoint.z;

        // Parallax mouse
        mouseX += (targetMouseX - mouseX) * CONFIG.mouseSmoothing;
        mouseY += (targetMouseY - mouseY) * CONFIG.mouseSmoothing;
        const parallaxX = mouseX * CONFIG.mouseParallaxStrength;
        const parallaxY = mouseY * CONFIG.mouseParallaxStrength;

        // rightVector dans _fv6
        _fv6.crossVectors(smoothedDirection, _fvUp).normalize();

        // parallaxOffset direct sur _fv5
        _fv5.addScaledVector(_fv6, parallaxX * 20);
        _fv5.y += parallaxY * 15;

        // Orbit
        const orbitActive = easedProgress >= CONFIG.orbitEnabledFromProgress;
        if (!orbitActive) {
            targetOrbitAngle += (0 - targetOrbitAngle) * 0.1;
            targetOrbitPitch += (0 - targetOrbitPitch) * 0.1;
        }
        orbitAngle += (targetOrbitAngle - orbitAngle) * CONFIG.orbitSmoothing;
        orbitPitch += (targetOrbitPitch - orbitPitch) * CONFIG.orbitSmoothing;
        orbitPitch = Math.max(CONFIG.orbitPitchMin, Math.min(CONFIG.orbitPitchMax, orbitPitch));
        orbitAngle = Math.max(-CONFIG.orbitAngleMax, Math.min(CONFIG.orbitAngleMax, orbitAngle));

        // orbitCenter = dummyLookAt, orbitOffset = _fv5 - orbitCenter
        _fv2.copy(_fv5).sub(dummyLookAt);
        _fv2.applyAxisAngle(_fvUp, orbitAngle);
        if (_fv6.lengthSq() > 0.0001) {
            _fv2.applyAxisAngle(_fv6, orbitPitch);
        }
        _fv5.copy(dummyLookAt).add(_fv2);

        camera.position.lerp(_fv5, CONFIG.camInertia);
        dummyLookAt.lerp(lookAtTarget, 0.05);
        camera.lookAt(dummyLookAt);
    }

    updateTimelineNavArrow();

    // --- RENDU CONDITIONNEL ---
    const easedDelta = Math.abs(easedProgress - _lastRenderedEasedProgress);
    const mouseDelta = Math.abs(mouseX - _lastRenderedMouseX) + Math.abs(mouseY - _lastRenderedMouseY);
    const orbitDelta = Math.abs(orbitAngle - _lastRenderedOrbitA) + Math.abs(orbitPitch - _lastRenderedOrbitP);

    const needsRender =
        _forceRenderFrames > 0 ||
        isUserActive ||
        anyStepAnimating ||
        isDragging ||
        easedDelta > 0.0001 ||
        mouseDelta > 0.001 ||
        orbitDelta > 0.0005;

    if (needsRender) {
        if (_forceRenderFrames > 0) _forceRenderFrames--;
        _lastRenderedEasedProgress = easedProgress;
        _lastRenderedMouseX = mouseX;
        _lastRenderedMouseY = mouseY;
        _lastRenderedOrbitA = orbitAngle;
        _lastRenderedOrbitP = orbitPitch;
        renderer.render(scene, camera);
    }
}

// Initialisation avec IntersectionObserver pour économiser les ressources
function setupLazyLoad() {
    const wrapper = document.getElementById('mm-fidelity-wrapper');
    if (!wrapper) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                if (!initialized) {
                    init();
                    if (renderer) {
                        initialized = true;
                    }
                } else if (!isAnimating) {
                    isAnimating = true;
                    _forceRenderFrames = 3;
                    requestAnimationFrame(animate);
                }
            } else {
                isAnimating = false;
            }
        });
    }, { rootMargin: '400px' });
    
    observer.observe(wrapper);
}

// Attendre que le DOM soit complètement chargé ou s'exécuter immédiatement
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupLazyLoad);
} else {
    setupLazyLoad();
}
